// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@conklin/App/Components/Notify";
import TitleTypography from "@conklin/App/Components/TitleTypography/TitleTypography";
import { xTabEnableZone } from "@conklin/App/Handlers/XNG/Common";
import DataTable from "@conklin/MobiusOverrides/DataTable/DataTable";
import DataTableHeader from "@conklin/MobiusOverrides/DataTable/DataTableHeader";
import Link from "@conklin/MobiusOverrides/Link/Link";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@conklin/Overrides/Components/OrderSummaryCard";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableBody, DataTableHead, DataTableRow } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Changes
        isDashboardPage: getCurrentPage(state).type === "MyAccountPage",
    };
};

const mapDispatchToProps = {
    loadOrders,
    // XNG-Changes
    xTabEnableZone,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    specificWidthDataTableHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    wrapper?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}`;

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC",
    // XNG-Changes
    expand: ["orderLines"],
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    // XNG-Changes
    componentDidUpdate(): void {
        if (this.props.canViewOrders && !this.props.ordersDataView.value && !this.props.ordersDataView.isLoading) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        // XNG-Changes
        this.props.xTabEnableZone({ zone: this.props.zone, isEnabled: true });

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        // XNG-Changes: structur
        return (
            <StyledWrapper {...customCssWrapper} {...styles.wrapper} data-test-selector="widget_RecentOrders">
                {this.props.isDashboardPage && (
                    <TitleTypography linkText={translate("View All Orders")} link={orderHistoryUrl} showIcon={true}>
                        {translate("Recent Orders")}
                    </TitleTypography>
                )}
                {ordersDataView.value.length === 0 && (
                    <Notify
                        type="warning"
                        fullWidth={true}
                        showClose={false}
                        data-test-selector="recentOrders_NoOrdersFound"
                    >
                        {siteMessage("Custom_No_Recent_Orders_Message")}
                    </Notify>
                )}
                {ordersDataView.value.length > 0 && (
                    <>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.dataTableHeaders} {...styles.specificWidthDataTableHeader}>
                                    {translate("Date")}
                                </DataTableHeader>
                                {/* XNG-Changes */}
                                {/* <DataTableHeader {...styles.dataTableHeaders}>{translate("Item Code")}</DataTableHeader> */}
                                {/* XNG-Changes */}
                                <DataTableHeader {...styles.dataTableHeaders} {...styles.specificWidthDataTableHeader}>
                                    {translate("Order Number")}
                                </DataTableHeader>
                                {/* XNG-Changes */}
                                {/* <DataTableHeader {...styles.dataTableHeaders}>{translate("SKU")}</DataTableHeader> */}
                                {/* XNG-Changes */}
                                {/* <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Product Name")}
                                </DataTableHeader> */}
                                {/* XNG-Changes */}
                                <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Ship-To/Pickup")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Price")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Reorder")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {ordersDataView.value.map(
                                    order =>
                                        order !== null && (
                                            <DataTableRow
                                                className="product-row"
                                                data-test-selector="recentOrders_dataLine"
                                                key={order.id}
                                            >
                                                <OrderSummaryCard
                                                    order={order}
                                                    orderSettings={settingsCollection.orderSettings}
                                                    extendedStyles={styles.orderSummaryCard}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                        {!this.props.isDashboardPage && (
                            <Link href={orderHistoryUrl} {...styles.viewAllLink}>
                                {translate("View All Orders")}
                            </Link>
                        )}
                    </>
                )}
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
